import { Box } from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import Button from "components/button/Button";
import dropDownStyle from "components/dropdown-menu/dropdown-menu.scss";
import { DropdownMenu, Position } from "components/dropdown-menu/DropdownMenu";
import ToggleablePanel from "components/header/ToggleablePanel";
import Chevron from "components/icons/Chevron";
import { DeviceTypeIconLaptop } from "components/icons/DeviceTypeIconLaptop";
import { DeviceTypeIconLooseDrive } from "components/icons/DeviceTypeIconLooseDrive";
import { DeviceTypeIconMobile } from "components/icons/DeviceTypeIconMobile";
import { DeviceTypeIconServer } from "components/icons/DeviceTypeIconServer";
import KebabIcon from "components/icons/KebabIcon";
import { OsIconAndroid } from "components/icons/OsIconAndroid";
import { OsIconLinux } from "components/icons/OsIconLinux";
import { OsIconMac } from "components/icons/OsIconMac";
import { OsIconWearOs } from "components/icons/OsIconWearOs";
import { OsIconWindows } from "components/icons/OsIconWindows";
import { subpageContext } from "components/layout/ApplicationLayout";
import MenuItemButton from "components/menu-item-button/MenuItemButton";
import { GettingStarted as BarGettingStarted } from "components/products/bar-home-page/GettingStarted";
import BdeGettingStarted from "components/products/bde-home-page/GettingStarted";
import BmdeGettingStarted from "components/products/bmde-home-page/GettingStarted";
import BmpOpGettingStarted from "components/products/bmpop-home-page/GettingStarted";
import BmweGettingStarted from "components/products/bmwe-home-page/GettingStarted";
import { WireIcon } from "components/products/home-icons/WireIcon";
import style from "components/products/product-card/product-card.scss";
import Tooltip from "components/tooltip/Tooltip";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import userMenuStyle from "components/user-menu/user-menu.scss";
import { DeviceType, LicenseData } from "domain/licenses";
import { getHomepageProductsMappingList, OnboardingProductType } from "domain/onboardings";
import {
    BmpHomeOperatingSystem,
    OnboardingImage,
    SoftwarePackage,
    SoftwarePackageHomePage,
} from "domain/softwarePackages";
import { getProductManualLanguage } from "services/product-manuals/productManualLanguageRepository";
import { Action, Category, Label, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie, isLicensingModelBundleWithToken } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import { ThemeName } from "store/theme";
import { resolveColor } from "utils/commonFunctions";
import { createShaChecksumMenuItem } from "utils/shaChecksumUtils";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    fetchedLicenses: LicenseData[];
    fetchedSoftwarePackages?: SoftwarePackage[];
    softwarePackages: SoftwarePackage[];
    name: string;
    bdeOnboarded: boolean;
    bmdeOnboarded: boolean;
    bmpOpOnboarded: boolean;
    barOnboarded: boolean;
    additionalProduct?: boolean;
    saveBmdeOnboarded: (onboarded: boolean) => void;
    saveBdeOnboarded: (onboarded: boolean) => void;
    saveBmpOpOnboarded: (onboarded: boolean) => void;
    saveBarOnboarded: (onboarded: boolean) => void;
    lastCompletedBdeStep: string;
    lastCompletedBmdeStep: string;
    lastCompletedBmpOpStep: string;
    lastCompletedBarStep: string;
    saveLastCompletedBmdeStep: (step: string) => void;
    saveLastCompletedBdeStep: (step: string) => void;
    saveLastCompletedBmpOpStep: (step: string) => void;
    saveLastCompletedBarStep: (step: string) => void;
    bmweOnboarded?: boolean;
    saveBmweOnboarded?: (onboarded: boolean) => void;
    lastCompletedBmweStep?: string;
    saveLastCompletedBmweStep?: (step: string) => void;
    halfSizedCard: boolean;
    fetchedOnboardingImages: OnboardingImage[];
    aiPanelCard?: boolean;
}

const ProductCard = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const context = React.useContext(subpageContext);
    const theme = useTheme();
    const ref = React.useRef<HTMLDivElement | null>(null);
    const location = useLocation();
    const themeName = useSelector((state: StoreState) => state.themeReducer.themeName);

    const [dropdownMenuPlacement, setDropdownMenuPlacement] = React.useState<Position>("AUTO");
    const [kebabIconDropdownMenuPlacement, setKebebIconDropdownMenuPlacement] = React.useState<Position>("AUTO");
    const [showGettingStarted, setShowGettingStarted] = React.useState(false);
    const [productType, setProductType] = React.useState<OnboardingProductType | undefined>(undefined);
    const [deviceType, setDeviceType] = React.useState<DeviceType | undefined>(undefined);
    const [description, setDescription] = React.useState<string>();
    const [onboarding, setOnboarding] = React.useState<OnboardingProductType>();
    const [onboardingTooltipDescription, setOnboardingTooltipDescription] = React.useState<string>();
    const [productVersions, setProductVersions] = React.useState<
        { version: string; downloadUrl: string; productName: string }[]
    >([]);
    const [manualsVersions, setManualsVersions] = React.useState<
        (
            | {
                  manualType: string;
                  version: string;
                  language: string;
                  link: string;
              }
            | undefined
        )[]
    >([]);

    React.useEffect(() => {
        const homepageProductsMappingList = getHomepageProductsMappingList(t);

        const product = homepageProductsMappingList.find((productItem) => productItem.productName === props.name);
        setDeviceType(product?.deviceType);
        setDescription(product?.description);
        setOnboarding(product?.onboarding as OnboardingProductType);
        setOnboardingTooltipDescription(product?.onboardingDescription);
        const productVersions = props.softwarePackages.flatMap((softwarePackage) =>
            softwarePackage.versions.map((version) => ({
                productName: softwarePackage.productName.replace(product?.productName ?? "", ""),
                version: version.version,
                downloadUrl: version.downloadUrl,
            }))
        );
        setProductVersions(productVersions);

        const manualVersions = Array.from(
            new Set(
                props.softwarePackages.flatMap(
                    (product: SoftwarePackageHomePage) =>
                        product.manualsDownloads?.versions.flatMap((version) =>
                            version.manuals.map((manual) =>
                                JSON.stringify({
                                    version: version.version,
                                    language: getProductManualLanguage(manual.language).title,
                                    link: manual.link,
                                    manualType: manual.type === "admin" ? t("Onboarding.home.adminLabel") : "",
                                })
                            )
                        ) ?? []
                )
            )
        ).map((item) => JSON.parse(item));

        setManualsVersions(manualVersions);
    }, []);

    const operatingSystems = new Set(props.softwarePackages.flatMap((item) => item.bmpHomeOperatingSystems ?? []));

    const getDeviceTypeIcon = () => {
        if (deviceType === DeviceType.LAPTOP) {
            return <DeviceTypeIconLaptop />;
        }

        if (deviceType === DeviceType.MOBILE) {
            return <DeviceTypeIconMobile />;
        }

        if (deviceType === DeviceType.LOOSE_DRIVES) {
            return <DeviceTypeIconLooseDrive />;
        }

        if (deviceType === DeviceType.SERVER) {
            return <DeviceTypeIconServer />;
        }
    };

    const getOsIcons = () => {
        const icons = [];

        if (operatingSystems.has(BmpHomeOperatingSystem.WINDOWS)) {
            icons.push(<OsIconWindows />);
        }

        if (
            operatingSystems.has(BmpHomeOperatingSystem.MAC) ||
            operatingSystems.has(BmpHomeOperatingSystem.IOS) ||
            operatingSystems.has(BmpHomeOperatingSystem.IPAD_OS)
        ) {
            icons.push(<OsIconMac />);
        }

        if (
            operatingSystems.has(BmpHomeOperatingSystem.CHROMIUM_OS) ||
            operatingSystems.has(BmpHomeOperatingSystem.AGNOSTIC)
        ) {
            icons.push(<OsIconLinux />);
        }

        if (operatingSystems.has(BmpHomeOperatingSystem.ANDROID)) {
            icons.push(<OsIconAndroid />);
        }

        if (operatingSystems.has(BmpHomeOperatingSystem.WEAR_OS)) {
            icons.push(<OsIconWearOs />);
        }

        return icons;
    };

    React.useEffect(() => {
        function updateDropdownPosition() {
            if (!ref.current) {
                return;
            }

            const rect = ref.current.getBoundingClientRect();

            const closeToWindowBottomEdge = window.innerHeight - rect.top < 400;
            const closeToWindowRightEdge = window.innerWidth - rect.width - rect.left < 400;

            if (closeToWindowBottomEdge) {
                setDropdownMenuPlacement("LEFT-TOP");
                setKebebIconDropdownMenuPlacement("RIGHT-TOP");
            } else if (closeToWindowRightEdge) {
                setDropdownMenuPlacement("LEFT");
                setKebebIconDropdownMenuPlacement("RIGHT");
            } else {
                setDropdownMenuPlacement("AUTO");
                setKebebIconDropdownMenuPlacement("AUTO");
            }
        }
        updateDropdownPosition();

        const resizeObserver = new ResizeObserver(updateDropdownPosition);
        if (ref.current) {
            resizeObserver.observe(ref.current);
        }

        window.addEventListener("resize", updateDropdownPosition);
        window.addEventListener("scroll", updateDropdownPosition, true);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", updateDropdownPosition);
            window.removeEventListener("scroll", updateDropdownPosition, true);
        };
    }, []);

    React.useEffect(() => {
        if (location.state) {
            const state = location.state as HomeState;
            if (state.showGettingStarted) {
                setShowGettingStarted(true);
                setProductType(state.productType as OnboardingProductType);
            }
        }
    }, [location]);

    React.useEffect(() => {
        if (showGettingStarted && productType !== undefined) {
            navigateToOnboardings(productType, true);
            setShowGettingStarted(false);
            window.history.replaceState({}, document.title);
        }
    }, [showGettingStarted, productType]);

    const opacity = [ThemeName.DEFAULT_DARK, ThemeName.SUB_TENANT_DARK, ThemeName.TENANT_DARK].includes(themeName)
        ? "0.11"
        : "1";

    const getOnboardingButton = () => {
        if (hasTenantCookie()) {
            return;
        }
        if (onboarding === OnboardingProductType.BDE && !props.bdeOnboarded) {
            return (
                <Button
                    variant={"PRIMARY"}
                    type={"button"}
                    isSmallButton
                    onClick={() => {
                        usageStatisticsService.sendEvent({
                            category: Category.ONBOARDING,
                            action: Action.GET_STARTED,
                            label: Label.BDE,
                        });
                        navigateToOnboardings(onboarding, props.bdeOnboarded);
                    }}
                    testId={testIds.homePage.productCard.getStartedButton}
                >
                    {getOnBoardingButtonText(props.lastCompletedBdeStep)}
                </Button>
            );
        }
        if (onboarding === OnboardingProductType.BMDE && !props.bmdeOnboarded) {
            return (
                <Button
                    variant={"PRIMARY"}
                    type={"button"}
                    isSmallButton
                    onClick={() => {
                        usageStatisticsService.sendEvent({
                            category: Category.ONBOARDING,
                            action: Action.GET_STARTED,
                            label: Label.BMDE,
                        });
                        navigateToOnboardings(onboarding, props.bmdeOnboarded);
                    }}
                    testId={testIds.homePage.productCard.getStartedButton}
                >
                    {getOnBoardingButtonText(props.lastCompletedBmdeStep)}
                </Button>
            );
        }
        if (onboarding === OnboardingProductType.BMPOP && !props.bmpOpOnboarded) {
            return (
                <Button
                    variant={"PRIMARY"}
                    type={"button"}
                    isSmallButton
                    onClick={() => {
                        usageStatisticsService.sendEvent({
                            category: Category.ONBOARDING,
                            action: Action.GET_STARTED,
                            label: Label.BMPOP,
                        });
                        navigateToOnboardings(onboarding, props.bmpOpOnboarded);
                    }}
                    testId={testIds.homePage.productCard.getStartedButton}
                >
                    {getOnBoardingButtonText(props.lastCompletedBmpOpStep)}
                </Button>
            );
        }
        if (onboarding === OnboardingProductType.BAR && !props.barOnboarded) {
            return (
                <Button
                    variant={"PRIMARY"}
                    type={"button"}
                    isSmallButton
                    onClick={() => {
                        usageStatisticsService.sendEvent({
                            category: Category.ONBOARDING,
                            action: Action.GET_STARTED,
                            label: Label.BAR,
                        });
                        navigateToOnboardings(onboarding, props.barOnboarded);
                    }}
                    testId={testIds.homePage.productCard.getStartedButton}
                >
                    {getOnBoardingButtonText(props.lastCompletedBarStep)}
                </Button>
            );
        }
        if (onboarding === OnboardingProductType.BMWE && !props.bmweOnboarded) {
            return (
                <Button
                    variant={"PRIMARY"}
                    type={"button"}
                    isSmallButton
                    onClick={() => {
                        usageStatisticsService.sendEvent({
                            category: Category.ONBOARDING,
                            action: Action.GET_STARTED,
                            label: Label.BMWE,
                        });
                        navigateToOnboardings(onboarding, props.bmweOnboarded ?? false);
                    }}
                    testId={testIds.homePage.productCard.getStartedButton}
                >
                    {getOnBoardingButtonText(props.lastCompletedBmweStep ?? "")}
                </Button>
            );
        }
    };

    function getOnBoardingButtonText(lastCompletedStep: string) {
        return lastCompletedStep === "" ? t("Onboarding.home.getStartedButton") : t("Onboarding.home.continueSetup");
    }

    function getOnboardingKebabMenuItemText(lastCompletedStep: string) {
        return lastCompletedStep === "VIEWING_REPORT"
            ? t("Onboarding.home.onboardingsReviewTooltip")
            : t("Onboarding.home.onboardingsViewTooltip");
    }

    function navigateToOnboardings(productType: OnboardingProductType, showBmpSetup: boolean) {
        switch (productType) {
            case OnboardingProductType.BDE:
                context?.setSubpage({
                    content: (
                        <BdeGettingStarted
                            licenses={productVersions}
                            fetchedSoftwarePackages={props.fetchedSoftwarePackages}
                            fetchedLicenses={props.fetchedLicenses}
                            saveBdeOnboarded={props.saveBdeOnboarded}
                            showBmpSetup={showBmpSetup}
                            saveLastCompletedStep={props.saveLastCompletedBdeStep}
                            fetchedOnboardingImages={props.fetchedOnboardingImages}
                        />
                    ),
                    isModal: true,
                });
                break;
            case OnboardingProductType.BMDE:
                context?.setSubpage({
                    content: (
                        <BmdeGettingStarted
                            licenses={productVersions}
                            fetchedSoftwarePackages={props.fetchedSoftwarePackages}
                            fetchedLicenses={props.fetchedLicenses}
                            saveBmdeOnboarded={props.saveBmdeOnboarded}
                            showBmpSetup={showBmpSetup}
                            saveLastCompletedStep={props.saveLastCompletedBmdeStep}
                            fetchedOnboardingImages={props.fetchedOnboardingImages}
                        />
                    ),
                    isModal: true,
                });
                break;
            case OnboardingProductType.BMPOP:
                context?.setSubpage({
                    content: (
                        <BmpOpGettingStarted
                            licenses={productVersions}
                            fetchedLicenses={props.fetchedLicenses}
                            showBmpSetup={showBmpSetup}
                            fetchedSoftwarePackages={props.fetchedSoftwarePackages}
                            saveLastCompletedStep={props.saveLastCompletedBmpOpStep}
                            saveBmpOpOnboarded={props.saveBmpOpOnboarded}
                            fetchedOnboardingImages={props.fetchedOnboardingImages}
                        />
                    ),
                    isModal: true,
                });
                break;
            case OnboardingProductType.BAR:
                context?.setSubpage({
                    content: (
                        <BarGettingStarted
                            licenses={productVersions}
                            fetchedSoftwarePackages={props.fetchedSoftwarePackages}
                            fetchedLicenses={props.fetchedLicenses}
                            saveBarOnboarded={props.saveBarOnboarded}
                            showBmpSetup={showBmpSetup}
                            saveLastCompletedStep={props.saveLastCompletedBarStep}
                            fetchedOnboardingImages={props.fetchedOnboardingImages}
                        />
                    ),
                    isModal: true,
                });
                break;
            case OnboardingProductType.BMWE:
                context?.setSubpage({
                    content: (
                        <BmweGettingStarted
                            licenses={productVersions}
                            fetchedSoftwarePackages={props.fetchedSoftwarePackages}
                            fetchedLicenses={props.fetchedLicenses}
                            saveBmweOnboarded={props.saveBmweOnboarded ?? (() => undefined)}
                            showBmpSetup={showBmpSetup}
                            saveLastCompletedStep={props.saveLastCompletedBmweStep ?? (() => undefined)}
                            fetchedOnboardingImages={props.fetchedOnboardingImages}
                        />
                    ),
                    isModal: true,
                });
                break;
            default:
                break;
        }
    }

    const checkForEmptyShaKey = (product: SoftwarePackage) => {
        return product.versions.find((item) => item.sha256 === "");
    };

    const toastForShaKeys = (product: SoftwarePackage, index: number, title: string) => {
        const shaValue = product.versions[0]?.sha256;
        if (!shaValue) {
            return null;
        }
        return createShaChecksumMenuItem(
            shaValue,
            product.productName,
            title,
            index,
            t,
            theme.iconFillColor,
            theme.whiteColor
        );
    };

    function hasCompletedOnBoarding(onboarding: OnboardingProductType | undefined) {
        if (onboarding === OnboardingProductType.BDE && props.bdeOnboarded) {
            return true;
        }
        if (onboarding === OnboardingProductType.BMDE && props.bmdeOnboarded) {
            return true;
        }
        if (onboarding === OnboardingProductType.BMPOP && props.bmpOpOnboarded) {
            return true;
        }
        if (onboarding === OnboardingProductType.BAR && props.barOnboarded) {
            return true;
        }
        if (onboarding === OnboardingProductType.BMWE && props.bmweOnboarded) {
            return true;
        } else {
            return false;
        }
    }

    function renderFooterSection() {
        const lastCompletedStepMap: Record<OnboardingProductType, string> = {
            [OnboardingProductType.BDE]: props.lastCompletedBdeStep,
            [OnboardingProductType.BMDE]: props.lastCompletedBmdeStep,
            [OnboardingProductType.BMPOP]: props.lastCompletedBmpOpStep,
            [OnboardingProductType.BAR]: props.lastCompletedBarStep,
            [OnboardingProductType.BMWE]: props.lastCompletedBmweStep ?? "",
        };

        const lastCompletedStep = onboarding ? lastCompletedStepMap[onboarding] : "";

        const onBoardingButton = onboarding && isLicensingModelBundleWithToken() ? getOnboardingButton() : undefined;

        const isOnboardingProduct =
            !hasTenantCookie() && Object.keys(OnboardingProductType).includes(onboarding as OnboardingProductType);
        const showNonBundleOnboardingItem = isOnboardingProduct && !isLicensingModelBundleWithToken();
        const showBundleOnboardingItem =
            isOnboardingProduct && isLicensingModelBundleWithToken() && hasCompletedOnBoarding(onboarding);

        let downloadButton;
        if (productVersions.length > 1) {
            downloadButton = (
                <ToggleablePanel
                    title={""}
                    testId={testIds.homePage.productCard.downloadsMenu.button}
                    menuType={"HEADER"}
                    icon={
                        <Button variant={"PRIMARY"} isSmallButton>
                            {t("Common.download")}
                        </Button>
                    }
                    hideChevron={true}
                >
                    <DropdownMenu
                        position={dropdownMenuPlacement}
                        testId={testIds.homePage.productCard.downloadsMenu.itself}
                        isScrollable
                        menuItems={productVersions.map((item, index) => (
                            <MenuItemButton
                                key={"product" + index}
                                tabIndex={0}
                                onClick={() => {
                                    window.open(item.downloadUrl, "_blank");
                                }}
                            >
                                {item.version} {item.productName}
                            </MenuItemButton>
                        ))}
                    />
                </ToggleablePanel>
            );
        } else {
            downloadButton = (
                <Button
                    variant={"PRIMARY"}
                    isSmallButton
                    onClick={() => window.open(productVersions[0].downloadUrl, "_blank")}
                    data-testid={testIds.homePage.productCard.downloadsMenu.button}
                >
                    {t("Common.download")}
                </Button>
            );
        }

        let kebabMenu;
        props.softwarePackages.map((item, index) => {
            if (!checkForEmptyShaKey(item)) {
                kebabMenu = (
                    <div className={style.iconButton}>
                        <ToggleablePanel
                            title={""}
                            testId={testIds.homePage.productCard.kebabMenu.button}
                            menuType={"HEADER"}
                            icon={<KebabIcon color={resolveColor("--theme-icon-fill-default")} />}
                            hideChevron={true}
                        >
                            <DropdownMenu
                                position={kebabIconDropdownMenuPlacement}
                                testId={testIds.homePage.productCard.kebabMenu.itself}
                                menuItems={[
                                    props.softwarePackages.length > 1 ? (
                                        <ToggleablePanel
                                            key={"downloadFile"}
                                            title={t("Onboarding.home.verifyDownloadFile")}
                                            testId={testIds.homePage.productCard.kebabMenu.versionListMenu.button}
                                            menuType={"HEADER"}
                                            buttonClass={style.menuButtonWithChevron}
                                            chevronSize={{ width: 14, height: 14 }}
                                            customToggleIcon={
                                                <Chevron
                                                    color={theme.textColor}
                                                    className={userMenuStyle.chevronRight}
                                                    width={16}
                                                    height={16}
                                                />
                                            }
                                            hideChevron
                                        >
                                            <DropdownMenu
                                                position={kebabIconDropdownMenuPlacement}
                                                testId={testIds.homePage.productCard.kebabMenu.versionListMenu.itself}
                                                className={classNames(
                                                    dropDownStyle.dropdownMenu && dropDownStyle.nestedDropDown
                                                )}
                                                menuItems={props.softwarePackages.map((item, index) => (
                                                    <>{toastForShaKeys(item, index, item.productName)}</>
                                                ))}
                                            />
                                        </ToggleablePanel>
                                    ) : (
                                        <>{toastForShaKeys(item, index, t("Onboarding.home.verifyDownloadFile"))}</>
                                    ),

                                    showNonBundleOnboardingItem ? (
                                        <MenuItemButton
                                            key={"product"}
                                            tabIndex={0}
                                            data-testid={
                                                testIds.homePage.productCard.kebabMenu.reviewGettingStartedButton
                                            }
                                            onClick={() => {
                                                navigateToOnboardings(onboarding as OnboardingProductType, true);
                                            }}
                                        >
                                            {getOnboardingKebabMenuItemText(lastCompletedStep)}
                                        </MenuItemButton>
                                    ) : (
                                        <></>
                                    ),
                                    showBundleOnboardingItem ? (
                                        <MenuItemButton
                                            key={"product"}
                                            tabIndex={0}
                                            data-testid={
                                                testIds.homePage.productCard.kebabMenu.reviewGettingStartedButton
                                            }
                                            onClick={() => {
                                                navigateToOnboardings(onboarding as OnboardingProductType, true);
                                            }}
                                        >
                                            {t("Onboarding.home.onboardingsReviewTooltip")}
                                        </MenuItemButton>
                                    ) : (
                                        <></>
                                    ),
                                    manualsVersions.length > 1 ? (
                                        <ToggleablePanel
                                            key={"viewManual"}
                                            title={t("Onboarding.home.viewManual")}
                                            testId={testIds.homePage.productCard.kebabMenu.manualsMenu.button}
                                            menuType={"HEADER"}
                                            buttonClass={style.menuButtonWithChevron}
                                            chevronSize={{ width: 14, height: 14 }}
                                            customToggleIcon={
                                                <Chevron
                                                    color={theme.textColor}
                                                    className={userMenuStyle.chevronRight}
                                                    width={16}
                                                    height={16}
                                                />
                                            }
                                            hideChevron
                                        >
                                            <DropdownMenu
                                                position={kebabIconDropdownMenuPlacement}
                                                testId={testIds.homePage.productCard.kebabMenu.manualsMenu.itself}
                                                className={classNames(
                                                    dropDownStyle.dropdownMenu && dropDownStyle.nestedDropDown
                                                )}
                                                isScrollable
                                                menuItems={manualsVersions.map((item, index) => {
                                                    if (item) {
                                                        return (
                                                            <MenuItemButton
                                                                key={"manual" + index}
                                                                tabIndex={0}
                                                                onClick={() => {
                                                                    window.open(item.link, "_blank");
                                                                }}
                                                            >
                                                                {item.version.split("-")[1]
                                                                    ? item.version.split("-")[1]
                                                                    : item.version}{" "}
                                                                {item.language}{" "}
                                                                {item.manualType ? "(" + item.manualType + ")" : ""}
                                                            </MenuItemButton>
                                                        );
                                                    }
                                                    return <></>;
                                                })}
                                            />
                                        </ToggleablePanel>
                                    ) : manualsVersions.length === 1 ? (
                                        <MenuItemButton
                                            key={"viewManualButton"}
                                            tabIndex={0}
                                            data-testid={testIds.homePage.productCard.kebabMenu.viewManualButton}
                                            onClick={() => {
                                                if (manualsVersions[0]) {
                                                    window.open(manualsVersions[0].link, "_blank");
                                                }
                                            }}
                                        >
                                            {t("Onboarding.home.viewManual")}
                                        </MenuItemButton>
                                    ) : (
                                        <></>
                                    ),
                                ]}
                            />
                        </ToggleablePanel>
                    </div>
                );
            }
        });

        return (
            <div className={style.actionItemsContainer}>
                <div className={style.actionButtonContainer}>{kebabMenu}</div>
                <div className={style.actionButtonContainer}>
                    {onBoardingButton}
                    {downloadButton}
                </div>
            </div>
        );
    }

    return props.additionalProduct ? (
        <div
            ref={ref}
            className={classNames({
                [style.productAdditionalCard]: true,
                [style.halfSizedCard]: props.halfSizedCard,
            })}
            data-testid={testIds.homePage.productCard.itself}
            data-type={props.name}
            data-ai-panel-card={props.aiPanelCard || undefined}
        >
            <div className={style.productAdditionalCardWrapper}>
                <div className={style.name}>{props.name}</div>
                <div className={style.versionWrapper}>
                    <div className={style.versionAdditionalProduct}>
                        {!props.aiPanelCard && (
                            <TextBlock disableBottomSpacing>
                                {productVersions[0] ? productVersions[0].version : null}
                            </TextBlock>
                        )}
                    </div>
                </div>
            </div>
            {renderFooterSection()}
        </div>
    ) : (
        <div
            className={style.productCard}
            ref={ref}
            data-testid={testIds.homePage.productCard.itself}
            data-type={props.name}
            data-ai-panel-card={props.aiPanelCard || undefined}
        >
            <div className={style.productCardWrapper}>
                <Box gap={"Xxs"}>
                    {getDeviceTypeIcon()}
                    <Box columnLayout>
                        <Box className={style.name}>{props.name}</Box>
                        <Box gap={"Xxs"} verticalAlignment={"Center"} className={style.versionInformation}>
                            {onboarding ? (
                                <Tooltip
                                    content={
                                        onboardingTooltipDescription
                                            ? onboardingTooltipDescription
                                            : t("Onboarding.home.automationTooltip")
                                    }
                                >
                                    <WireIcon
                                        backgroundColor={theme.homeWireIconBackgroundColor}
                                        color={theme.homeWireIconColor}
                                        opacity={opacity}
                                    />
                                </Tooltip>
                            ) : null}
                            {productVersions[0] ? productVersions[0].version : null}
                            {getOsIcons()}
                        </Box>
                    </Box>
                </Box>
                <Box>{description}</Box>
            </div>
            {renderFooterSection()}
        </div>
    );
};

interface HomeState {
    showGettingStarted?: boolean;
    productType?: "BDE" | "BMDE" | "BMPOP" | "BAR" | "BMWE" | undefined;
}

export default ProductCard;
